import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  downloadCSV,
  downloadPDF,
  exportWithConfirmation,
} from "../utils/exportUtils";
import Loader from "../components/Loader";
import { toast } from "react-toastify";
import DeleteModal from "../components/DeleteModal.jsx/DeleteModal";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const allColumns = [
  { key: "name", label: "Name" },
  { key: "color", label: "Color" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Updated At" },
];

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [deletLoading, setDeleteLoading] = useState(false);
  const [departmentId, setDepartmentId] = useState([]);
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(50);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map((col) => col.key)
  );
  const [showColDropdown, setShowColDropdown] = useState(false);

  // State for table sorting
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle table sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/departments`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setDepartments(Array.isArray(data) ? data : []);
        setLoading(false);
      });
  }, []);

  const filtered = departments
    .filter((d) => !d.deleted_at)
    .filter((d) => d.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (sortField === "name") {
        aValue = a.name || "";
        bValue = b.name || "";
      } else if (sortField === "color") {
        aValue = a.color || "";
        bValue = b.color || "";
      } else if (sortField === "created_at") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else if (sortField === "updated_at") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const handleShow = (id) => navigate(`/departments/${id}`);
  const handleEdit = (id) => navigate(`/departments/${id}/edit`);
  const handleRemove = (id) => {
    setDeleteLoading(true);

    fetch(`${process.env.REACT_APP_API_URL}/api/departments/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.requiresConfirmation) {
          // Show confirmation dialog with related records info

          // User confirmed, make the confirmed deletion request
          fetch(data.confirmUrl, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          })
            .then((res) => res.json())
            .then((deleteData) => {
              if (deleteData.message) {
                setDepartments(departments.filter((d) => d.id !== id));
              } else {
                setDeleteLoading(true);
                toast.error("Failed to delete department");
              }
            })
            .catch((error) => {
              setDeleteLoading(true);
              console.error("Error deleting department:", error);
              toast.error("Failed to delete department");
            });
        } else if (data.message) {
          // Direct deletion successful
          setDepartments(departments.filter((d) => d.id !== id));
          toast.success("Department deleted successfully");
          setIsOpenDeleteModal(false);
          setDeleteLoading(true);
        } else {
          toast.error("Failed to delete department");
        }
      })
      .catch((error) => {
        console.error("Error deleting department:", error);
        toast.error("Failed to delete department");
      });
  };

  // Export handlers using shared utility
  const handleExportCSV = () => {
    downloadCSV(filtered, "departments.csv");
  };

  const handleExportPDF = () => {
    downloadPDF(filtered, "departments.pdf");
  };

  const handleColDropdownToggle = () => setShowColDropdown((v) => !v);

  const handleColDropdownBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setShowColDropdown(false);
  };

  if (loading) return <Loader />;

  return (
    <div className="container-fluid users-page">
      <div className="breadcrumb-row">
        <span 
          className="breadcrumb-main"
          onClick={() => navigate('/dashboard')}
          style={{ cursor: 'pointer' }}
        >
          Dashboard
        </span>
        <span className="breadcrumb-sep">/</span>
        <span 
          className="breadcrumb-users"
          onClick={() => navigate('/users')}
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          Users
        </span>
        <span className="breadcrumb-sep">/</span>
        <span 
          className="breadcrumb-staff"
          onClick={() => navigate('/staff')}
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          Staff
        </span>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-departments">Departments</span>
      </div>
      <DeleteModal
        isOpen={isOpenDeleteModal}
        onClose={() => setIsOpenDeleteModal(false)}
        onConfirm={() => handleRemove(departmentId)}
        isLoading={deletLoading}
      />
      <div className="users-overview-container">
        {/* <div className="staff-list-title">Departments</div> */}
        <div className="users-table-header-row">
          <div className="users-table-title">Departments</div>
          <button
            className="btn btn-add-user"
            onClick={() => navigate("/departments/create")}
          >
            {/* <FaPlus style={{ marginRight: 8 }} /> Add Department */}+ Add
            Department
          </button>
        </div>
        <div className="users-table-toolbar">
          <div
            style={{
              display: "inline-block",
              position: "relative",
              marginRight: 16,
            }}
          >
            {/* <button
              className="btn btn-col-visibility"
              onClick={handleColDropdownToggle}
              tabIndex={0}
              style={{ minWidth: 120 }}
            >
              Column visibility
            </button> */}
            {showColDropdown && (
              <div
                className="col-visibility-dropdown"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  background: "#eaffff",
                  border: "1px solid #aaa",
                  borderRadius: 4,
                  zIndex: 10,
                  minWidth: 140,
                  padding: 8,
                  marginTop: 2,
                }}
                tabIndex={-1}
                onBlur={handleColDropdownBlur}
              >
                {allColumns.map((col) => (
                  <div
                    key={col.key}
                    style={{
                      padding: "2px 8px",
                      cursor: "pointer",
                      background: visibleColumns.includes(col.key)
                        ? "#d0f0f0"
                        : "transparent",
                      borderRadius: 2,
                      fontWeight: visibleColumns.includes(col.key)
                        ? "bold"
                        : "normal",
                    }}
                    onClick={() => {
                      setVisibleColumns((cols) =>
                        cols.includes(col.key)
                          ? cols.filter((c) => c !== col.key)
                          : [...cols, col.key]
                      );
                    }}
                  >
                    {col.label}
                  </div>
                ))}
                <div
                  style={{
                    padding: "2px 8px",
                    color: "#888",
                    cursor: "not-allowed",
                    borderRadius: 2,
                    fontWeight: "bold",
                  }}
                >
                  Actions
                </div>
              </div>
            )}
          </div>
          <div className="users-table-entries">
            Show
            <select
              className="entries-select"
              value={entries}
              onChange={(e) => setEntries(Number(e.target.value))}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            entries
          </div>
          <div className="users-table-search-departments">
            Search:
            <input
              type="text"
              className="form-control"
              placeholder=""
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            className="btn departments-page btn-export-csv"
            style={{ float: "right", marginRight: 8 }}
            onClick={handleExportCSV}
          >
            Export as CSV
          </button>
          <button
            className="btn departments-page btn-export-pdf"
            style={{ float: "right", marginRight: 8 }}
            onClick={handleExportPDF}
          >
            Export as PDF
          </button>
          <button
            className="btn btn-col-visibility"
            onClick={handleColDropdownToggle}
            tabIndex={0}
            style={{ minWidth: 120 }}
          >
            Column visibility
          </button>
          {/* <button className="btn btn-add-user" style={{ float: 'right' }} onClick={() => navigate('/departments/create')}>
            + Add Department
          </button> */}
        </div>

        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                {visibleColumns.includes("name") && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("name")}
                    style={{ cursor: "pointer" }}
                  >
                    Name<SortIcon direction={sortField === "name" ? sortDirection : null} isActive={sortField === "name"} />
                  </th>
                )}
                {visibleColumns.includes("color") && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("color")}
                    style={{ cursor: "pointer" }}
                  >
                    Color<SortIcon direction={sortField === "color" ? sortDirection : null} isActive={sortField === "color"} />
                  </th>
                )}
                {visibleColumns.includes("created_at") && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("created_at")}
                    style={{ cursor: "pointer" }}
                  >
                    Created At<SortIcon direction={sortField === "created_at" ? sortDirection : null} isActive={sortField === "created_at"} />
                  </th>
                )}
                {visibleColumns.includes("updated_at") && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("updated_at")}
                    style={{ cursor: "pointer" }}
                  >
                    Updated At<SortIcon direction={sortField === "updated_at" ? sortDirection : null} isActive={sortField === "updated_at"} />
                  </th>
                )}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, entries).map((d, idx) => (
                <tr key={d.id}>
                  {visibleColumns.includes("name") && <td>{d.name}</td>}
                  {visibleColumns.includes("color") && (
                    <td>
                      <span
                        className="label"
                        style={{
                          backgroundColor: d.color,
                          color: "#fff",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "0.85em",
                        }}
                      >
                        {d.color}
                      </span>
                    </td>
                  )}
                  {visibleColumns.includes("created_at") && (
                    <td>
                      {d.created_at
                        ? new Date(d.created_at).toLocaleDateString()
                        : ""}
                    </td>
                  )}
                  {visibleColumns.includes("updated_at") && (
                    <td>
                      {d.updated_at
                        ? new Date(d.updated_at).toLocaleDateString()
                        : ""}
                    </td>
                  )}
                  <td>
                    <button
                      className="btn-action show"
                      onClick={() => handleShow(d.id)}
                    >
                      Show
                    </button>
                    <button
                      className="btn-action edit"
                      onClick={() => handleEdit(d.id)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-action remove"
                      onClick={() => {
                        setDepartmentId(d.id);
                        setIsOpenDeleteModal(true);
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 16 }}>
          Showing {filtered.length === 0 ? 0 : 1} to{" "}
          {Math.min(entries, filtered.length)} of {filtered.length} entries
        </div>
      </div>
    </div>
  );
};

export default Departments;
